import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { filingAPI, filingSupportingDocAPI, filingWorkflowAPI } from '../../utilities/api';
import { Filing } from '../../utilities/interfaces/Filing';
import { DocumentType } from '../../utilities/enums/DocumentType';

// represents the shape of the slice in state
interface FilingState {
  value: any;
  fetchingFilings: boolean;
  filing: any;
  filingWorkflow: any;
  filingSupportingDocs: any;
  filingHearingDocs: any;
  filingSummaryDocs: any;
  filingAffidavitDocs: any;
  filingFiscalDocs: any;
  filingExplanatoryDocs: any;
  filingGPRODocs: any;
  filingCoverLetterDocs: any;
  filingCertificateOfAdoptionDocs: any;
  dashboardFilings: any;
  fetchingDashboardFilings: boolean;
  historicalFilings: any;
  fetchingHistoricalFilings: boolean;
  savingFiling: boolean;
}

const initialState: FilingState = {
  value: [],
  fetchingFilings: false,
  filing: {},
  filingWorkflow: [],
  filingSupportingDocs: [],
  filingHearingDocs: [],
  filingSummaryDocs: [],
  filingAffidavitDocs: [],
  filingFiscalDocs: [],
  filingGPRODocs: [],
  filingCoverLetterDocs: [],
  filingExplanatoryDocs: [],
  filingCertificateOfAdoptionDocs: [],
  dashboardFilings: [],
  fetchingDashboardFilings: false,
  historicalFilings: [],
  fetchingHistoricalFilings: false,
  savingFiling: false,
};

const filingSlice = createSlice({
  name: 'stateFiling',
  initialState,
  reducers: {
    // add new filing item
    addFilingItem(state, action: PayloadAction<Filing>) {
      // its okay to do this because immer makes it immutable
      state.value.push(action.payload);
    },
    clearFilingList(state) {
      state.value = [];
    },
    clearDashboardFilings(state) {
      state.dashboardFilings = [];
    }
  },
  extraReducers(builder) {
    // #region filings
    builder.addCase(getAllFilings.fulfilled, (state, action) => {
      state.value = action.payload;
    });
    builder.addCase(getFilingsByFormId.pending, (state) => {
      state.fetchingFilings = true;
      state.value = [];
    });
    builder.addCase(getFilingsByFormId.fulfilled, (state, action) => {
      state.value = action.payload;
      state.fetchingFilings = false;
    });
    builder.addCase(getFilingsByFormId.rejected, (state) => {
      state.fetchingFilings = false;
    })
    builder.addCase(createFiling.pending, (state) => {
      state.savingFiling = true;
    });
    builder.addCase(createFiling.fulfilled, (state, action) => {
      state.value.push(action.payload);
      state.savingFiling = false;
    });
    builder.addCase(createFiling.rejected, (state) => {
      state.savingFiling = false;
    });
    builder.addCase(updateFiling.pending, (state) => {
      state.savingFiling = true;
    });
    builder.addCase(updateFiling.fulfilled, (state, action) => {
      state.value.forEach((filing: any, index: number) => {
        if (filing.id === action.payload.id) {
          state.value[index] = action.payload;
        }
      });
      state.savingFiling = false;
    });
    builder.addCase(updateFiling.rejected, (state) => {
      state.savingFiling = false;
    });
    builder.addCase(getFilingById.fulfilled, (state, action) => {
      state.filing = action.payload;
    });
    builder.addCase(getDashboardTableFilings.pending, (state) => {
      state.fetchingDashboardFilings = true;
    })
    builder.addCase(getDashboardTableFilings.fulfilled, (state, action) => {
      state.dashboardFilings = action.payload;
      state.fetchingDashboardFilings = false;
    });
    builder.addCase(getDashboardTableFilings.rejected, (state) => {
      state.fetchingDashboardFilings = false;
    })
    // #endregion
    // #region filing worflows
    builder.addCase(getAllFilingWorkflows.fulfilled, (state, action) => {
      state.filingWorkflow = action.payload;
    });
    // #endregion
    //#region filing supporting docs
    builder.addCase(getFilingSupportingDocs.fulfilled, (state, action: any) => {
      // clear data if multiple requests come in before the previous is fulfilled so we don't get duplicate data
      state.filingSupportingDocs = [];
      state.filingAffidavitDocs = [];
      state.filingHearingDocs = [];
      state.filingSummaryDocs = [];
      state.filingFiscalDocs = [];
      state.filingExplanatoryDocs = [];
      state.filingGPRODocs = [];
      state.filingCoverLetterDocs = [];
      state.filingCertificateOfAdoptionDocs = [];

      // split the filing documents into their respective categories based on type
      if (action.payload.length < 1) {
        state.filingSupportingDocs = false;
      } else {
        for (let i = 0; i < action.payload.length; i++) {

          switch (action.payload[i].documentTypeId){
            case DocumentType['Affidavit of Publication']:
              state.filingAffidavitDocs.push(action.payload[i]);
              break;

            case DocumentType['Hearing Summary']:
              state.filingSummaryDocs.push(action.payload[i]);
              break;

            case DocumentType['Hearing Notice']:
              state.filingHearingDocs.push(action.payload[i]);
              break;

            case DocumentType['Fiscal Impact Statement']:
              state.filingFiscalDocs.push(action.payload[i]);
              break;

            case DocumentType['Explanatory Statement']:
              state.filingExplanatoryDocs.push(action.payload[i]);
              break;

            case DocumentType['Gov Checklist']:
              state.filingGPRODocs.push(action.payload[i]);
              break;

            case DocumentType['Cover Letters and Statements']:
              state.filingCoverLetterDocs.push(action.payload[i]);
              break;

            case DocumentType['Certificate of Adoption']:
              state.filingCertificateOfAdoptionDocs.push(action.payload[i]);
              break;

            case DocumentType.Misc:
              state.filingSupportingDocs.push(action.payload[i]);
              break;
          }
        }
      }
    });
    // builder.addCase(getFilingSupportingDocs.rejected, (state, action: any) => {
    //   state.filingSupportingDocs = false;
    //   state.filingAffidavitDocs = false;
    //   state.filingHearingDocs = false;
    //   state.filingSummaryDocs = false;
    // });
    builder.addCase(getFilingSupportingDocs.pending, (state) => {
      state.filingSupportingDocs = [];
      state.filingAffidavitDocs = [];
      state.filingHearingDocs = [];
      state.filingSummaryDocs = [];
    });
    // #endregion
    builder.addCase(getHistoricalFilings.pending, (state) => {
      state.fetchingDashboardFilings = true;
    });
    builder.addCase(getHistoricalFilings.fulfilled, (state, action) => {
      state.fetchingDashboardFilings = false;
      state.historicalFilings = action.payload;
    });
    builder.addCase(getHistoricalFilings.rejected, (state) => {
      state.fetchingDashboardFilings = false;
    });
  },
});

// #region filings
// Async thunk to fetch data from api
// under extra reducers on fulfilled the redux store is updated
export const getAllFilings = createAsyncThunk('stateFiling/getFilingsItems', async () => {
    return await filingAPI.getFilings();
});

export const getFilingsByFormId = createAsyncThunk(
  'stateFiling/getFilingsByFormId',
  async (formId: number) => {
    let data: any = await filingAPI.getFilingsByFormId(formId);
    return data ? data : [];
  }
);

export const getFilingById = createAsyncThunk(
  'stateFiling/getFilingById',
  async (filingId: number) => {
    return await filingAPI.getFilingById(filingId);
  }
);

export const createFiling = createAsyncThunk('stateFiling/createFiling', async (filing: Filing) => {
  await filingAPI.createFiling(filing);
  return filing;
});

export const updateFiling = createAsyncThunk('stateFiling/updateFiling', async (filing: Filing) => {
  await filingAPI.updateFiling(filing);
  return filing;
});

export const getDashboardTableFilings = createAsyncThunk('stateFiling/getDashboardTableFilings', async () => {
  let response: any = await filingAPI.getDashboardTableFilings();
  return response;
});
//#endregion

// #region filing workflows
export const getAllFilingWorkflows = createAsyncThunk(
  'stateFiling/getFilingWorkflows',
  async () => {
    return await filingWorkflowAPI.getAllFilingWorkflows();
  }
);
// #endregion

// #region filing supporting docs
export const getFilingSupportingDocs = createAsyncThunk(
  'stateFiling/getFilingSupportingDocs',
  async (filingId: number) => {
    return await filingSupportingDocAPI.getFilingSupportingDocByFilingId(filingId);
  }
);
// #endregion

export const getHistoricalFilings = createAsyncThunk('stateFilings/getHistoricalFilings', async () => {
  return await filingAPI.getHistorical();
});

export const { addFilingItem, clearFilingList, clearDashboardFilings } = filingSlice.actions;
export default filingSlice.reducer;
